import { useState } from 'react';
import { Card, Button, Col, Row, Form } from 'react-bootstrap';
import { OrderForm, OrderFormRow } from '../shared/OrderForm';
import { t } from '../../services/translationService';
import { AddToCartRequestItem, CartItemResponse, addItemsToCart } from '../../services/CartService';
import { setLoader } from '../../redux/reducers/loaderSlice.reducer';
import { useDispatch } from 'react-redux';
import { setCartSize } from '../../redux/reducers/cartSlice.reducer';
import NotificationClass from '../../typescript/classes/NotificationClass';
import { NotificationTypes } from "../../typescript/enums/enums";
import { useErrorHandler } from '../../hooks/useErrorHandler';

export const BulkOrder = () => {
  const dispatch = useDispatch();
  const handleError = useErrorHandler();

  const [rows, setRows] = useState<OrderFormRow[]>([]);

  const [excelData, setExcelData] = useState('');
  const [hasClientErrors] = useState(false);
  const [hasServerErrors] = useState(false);

  const [canAddToCart, setCanAddToCart] = useState(false);



  const clearForm = () => {
    setRows([
      {
        catalogEntryCode: '',
        quantity: undefined,
        distributors: [],
        selectedDistributorShortName: '',
        title: '',
        reference: '',
      },
    ]);
  }

  const addAll = () => {
    const cartItems: OrderFormRow[] = excelData
      .split('\n')
      .map((row) => row.split('\t'))
      .map((row) => ({
        catalogEntryCode: row[0],
        quantity: +row[1],
        selectedDistributorShortName: row[2],
        reference: row[3],
      } as OrderFormRow))
      .filter(row => row.catalogEntryCode && !Number.isNaN(row.quantity));

    setRows(cartItems);
    setExcelData("");
  }

  const handleSubmit = async () => {
    dispatch(setLoader(true));

    const items = rows.map((row: OrderFormRow): AddToCartRequestItem => ({
      catalogEntryCode: row.catalogEntryCode,
      includeInOrder: true,
      quantity: row.quantity,
      reference: row.reference,
      selectedDistributorShortName: row.selectedDistributorShortName
    }));

    await addItemsToCart(items)
      .then((response) => {
        clearForm();

        // update size on cart badge
        dispatch(setCartSize(response
          .map((item: CartItemResponse) => item.quantity)
          .reduce((previousValue: number, currentValue: number) => previousValue + currentValue, 0)));

        NotificationClass.createNotification({
          type: NotificationTypes.Success,
          message: t('itemsAddedToCart')
        });
      })
      .catch((error) => {
        // Handle error using the centralized handleError function
        handleError(error);
      })
      .finally(() => {
        // Ensure loader is hidden regardless of success or error
        dispatch(setLoader(false));
      });
  }

  return (
    <>
      <Card className="nopadding">
        <Card.Header className="p-3 border-0">
          <Row>
            <Col sm={4}>
              <p className="text-muted mb-3 u-text-include-linebreak">
                {t('massOrderInstructions')}
              </p>
            </Col>
            <Col sm={8} className="d-flex flex-column align-items-end">
              <Form.Control
                className="u-text-monospace-pre"
                as="textarea"
                value={excelData}
                onChange={(e) =>
                  setExcelData((e.target as HTMLTextAreaElement).value)
                }
                isInvalid={hasClientErrors}
              />
              <div className="d-flex justify-content-between w-100">
                <span>
                  {hasClientErrors && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {t('missingArticleNumber')}
                    </Form.Control.Feedback>
                  )}
                </span>
                <div className="d-flex gap-3">
                  <Button className="mt-3" variant="primary" onClick={addAll}>
                    {t('addAll')}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Header>
        {!hasServerErrors && rows.length > 0 && (
          <>
            <Card.Body>
              <OrderForm rows={rows} setRows={setRows} isValid={setCanAddToCart} populated={true} autoPopulate={true} />
            </Card.Body>
            <Card.Footer>
              <Button
                onClick={handleSubmit}
                disabled={rows.some((r: any) => r.invalid) || !canAddToCart}
              >
                {t('addAll')}
              </Button>
            </Card.Footer>
          </>
        )}
      </Card>
    </>
  );
};
